import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, EMPTY, filter, Subject, switchMap } from 'rxjs';
import { LocalStorageService } from '@suite/local-storage-data-access';
import { ClientSettingsService } from '@suite/client-settings-data-access';
import { AppStorageClearRequest, AppStorageRemoveRequest, AppStorageSetRequest } from '@suite/app-storage-models';

/**
 * App storage service that provides localStorage-like API with declarative sync.
 *
 * This service uses localStorage as the single source of truth, storing all data
 * in one object. Changes to localStorage automatically sync to client settings
 * for cross-device synchronization.
 *
 * Architecture:
 * - localStorage: Primary storage, immediate updates
 * - clientSettings: Secondary storage, debounced sync for server persistence
 * - Offline-first: Works immediately, syncs when possible
 */
@Injectable({ providedIn: 'root' })
export class AppStorageService {
  private localStorageService = inject(LocalStorageService);
  private clientSettingsService = inject(ClientSettingsService);

  // Single storage key for all app data
  private readonly STORAGE_KEY = 'app-storage';

  // Current state signal
  private state = signal<Record<string, unknown>>({});

  // Subject for state changes (to create observable stream)
  private stateChange$ = new Subject<Record<string, unknown>>();

  // Command subjects
  setItem$ = new Subject<AppStorageSetRequest>();
  removeItem$ = new Subject<AppStorageRemoveRequest>();
  clear$ = new Subject<AppStorageClearRequest>();

  constructor() {
    // Initialize state from localStorage
    this.initializeState();

    // Set up signal effect to emit state changes
    effect(() => {
      const currentState = this.state();
      this.stateChange$.next(currentState);
    });

    // Set up declarative sync: localStorage changes → clientSettings
    this.setupDeclarativeSync();

    // Handle imperative commands
    this.setupCommandHandlers();
  }

  // Public API (localStorage-like interface)

  /**
   * Gets an item from storage by key (supports dot notation).
   */
  getItem(key: string): string | null {
    const value = untracked(() => this.getNestedValue(this.state(), key));
    if (value === undefined) {
      return null;
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  /**
   * Gets the raw value (not stringified) for a key.
   */
  getValue(key: string, defaultValue?: unknown): unknown {
    return untracked(() => this.getNestedValue(this.state(), key)) ?? defaultValue;
  }

  /**
   * Gets the key at the specified index.
   * Returns flattened keys from the storage object.
   */
  key(index: number): string | null {
    const keys = this.getKeys();
    return keys[index] || null;
  }

  /**
   * Gets the number of top-level keys in storage.
   */
  get length(): number {
    return untracked(() => Object.keys(this.state()).length);
  }

  /**
   * Checks if a key exists in storage (supports dot notation).
   */
  hasItem(key: string): boolean {
    return untracked(() => this.getNestedValue(this.state(), key)) !== undefined;
  }

  /**
   * Gets all flattened keys from the storage object.
   */
  getKeys(): string[] {
    return untracked(() => this.flattenKeys(this.state()));
  }

  // Private implementation

  /**
   * Initialize state from localStorage on startup.
   */
  private initializeState(): void {
    const stored = this.localStorageService.getValue(this.STORAGE_KEY, {});
    this.state.set(typeof stored === 'object' && stored !== null ? (stored as Record<string, unknown>) : {});
  }

  /**
   * Set up declarative sync: localStorage → clientSettings
   */
  private setupDeclarativeSync(): void {
    // Watch state changes and sync to clientSettings
    this.stateChange$
      .pipe(
        // Only sync when state actually changes
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        // Debounce to avoid excessive API calls
        debounceTime(500),
        // Skip empty states
        filter((state) => Object.keys(state).length > 0),
        // Sync to client settings
        switchMap((state) => this.syncToClientSettings(state)),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: () => console.debug('AppStorage: Successfully synced to client settings'),
        error: (error) => console.error('AppStorage: Failed to sync to client settings:', error),
      });
  }

  /**
   * Handle imperative commands (setItem$, removeItem$, clear$)
   */
  private setupCommandHandlers(): void {
    // Handle setItem commands
    this.setItem$
      .pipe(
        switchMap((request) => {
          try {
            this.setItemSync(request.key, request.value);
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Handle removeItem commands
    this.removeItem$
      .pipe(
        switchMap((request) => {
          try {
            this.removeItemSync(request.key);
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Handle clear commands
    this.clear$
      .pipe(
        switchMap((request) => {
          try {
            this.clearSync();
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /**
   * Synchronously sets an item in localStorage and updates state.
   * ClientSettings sync happens automatically via declarative sync.
   */
  private setItemSync(key: string, value: string): void {
    const currentState = { ...untracked(() => this.state()) };
    this.setNestedValue(currentState, key, value);

    // Update localStorage (single source of truth)
    this.localStorageService.setItem$.next({
      key: this.STORAGE_KEY,
      value: currentState,
      onError: (error) => console.error('AppStorage: Failed to set localStorage item:', error),
    });

    // Update state signal
    this.state.set(currentState);
  }

  /**
   * Synchronously removes an item from localStorage and updates state.
   */
  private removeItemSync(key: string): void {
    const currentState = { ...untracked(() => this.state()) };
    this.removeNestedValue(currentState, key);

    // Update localStorage
    this.localStorageService.setItem$.next({
      key: this.STORAGE_KEY,
      value: currentState,
      onError: (error) => console.error('AppStorage: Failed to update localStorage:', error),
    });

    // Update state signal
    this.state.set(currentState);
  }

  /**
   * Synchronously clears all items from localStorage and updates state.
   */
  private clearSync(): void {
    const emptyState = {};

    // Clear localStorage
    this.localStorageService.removeValue$.next({
      key: this.STORAGE_KEY,
      onError: (error) => console.error('AppStorage: Failed to clear localStorage:', error),
    });

    // Update state signal
    this.state.set(emptyState);
  }

  /**
   * Sync current state to client settings.
   */
  private syncToClientSettings(state: Record<string, unknown>) {
    return new Promise<void>((resolve, reject) => {
      this.clientSettingsService.saveSettings$.next({
        value: state,
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  }

  // Utility methods for nested object manipulation

  /**
   * Get a nested value from an object using dot notation.
   */
  private getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: unknown, key: string) => {
      return current && typeof current === 'object' && key in current
        ? (current as Record<string, unknown>)[key]
        : undefined;
    }, obj);
  }

  /**
   * Set a nested value in an object using dot notation.
   */
  private setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;

    const target = keys.reduce((current: Record<string, unknown>, key: string) => {
      if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
        current[key] = {};
      }
      return current[key] as Record<string, unknown>;
    }, obj);

    target[lastKey] = value;
  }

  /**
   * Remove a nested value from an object using dot notation.
   */
  private removeNestedValue(obj: Record<string, unknown>, path: string): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;

    const target = keys.reduce((current: unknown, key: string) => {
      return current && typeof current === 'object' && key in current
        ? (current as Record<string, unknown>)[key]
        : undefined;
    }, obj);

    if (target && typeof target === 'object') {
      delete (target as Record<string, unknown>)[lastKey];
    }
  }

  /**
   * Flatten an object into dot-notation keys.
   */
  private flattenKeys(obj: Record<string, unknown>, prefix = ''): string[] {
    const keys: string[] = [];

    Object.keys(obj).forEach((key) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        keys.push(...this.flattenKeys(value as Record<string, unknown>, fullKey));
      } else {
        keys.push(fullKey);
      }
    });

    return keys;
  }
}
